import { formatDate, NgFor, NgIf, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';

import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  FeedIntakeUnit,
  WaterIntakeUnit,
} from 'src/app/reference-value-sheet/models/reference-value-sheet.model';
import { UnitOfMeasureSettings } from 'src/app/settings/models/setting.model';
import { getDayOffset } from 'src/app/settings/store/selectors/setting.selector';
import { DateHelper } from 'src/app/shared/helpers/dateHelper';
import { upsertObservationsForCycleRequest } from 'src/app/within-cycle-performance/store/actions/within-cycle-performance.actions';

import { WDORegistrationFormComponent } from './wdo-registration-form.component';
import Utils from '../../../shared/utils';
import {
  Barn,
  Cycle,
  DayOfWeekEnum,
  DayOldChick,
  DayRegistration,
  ValueRegistration,
  WDOsettingEnum,
  WeekRegistration,
} from '../../models/cycle.model';
import { CycleService } from '../../services/cycle.service';
import { litterQualityValidator } from '../../validators/litterquality-validator';

export interface WDORegistrationDialogInputData extends AbstractDialogData {
  selectedBarn?: Barn;
  barns?: Barn[];
  weekRegistrations: WeekRegistration[];
  cycle: Cycle;
  isDetailedView: boolean;
  unitOfMeasureSettings: UnitOfMeasureSettings;
}

export interface WDORegistrationDialogResultData {
  cycle: Cycle;
}

@Component({
  selector: 'app-wdo-registration',
  templateUrl: './wdo-registration.component.html',
  styleUrls: ['./wdo-registration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogComponent,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    NgFor,
    MatOption,
    NgIf,
    MatTabGroup,
    NgClass,
    MatTab,
    WDORegistrationFormComponent,
    MatIcon,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class WDORegistrationComponent extends AbstractDialog<WDORegistrationDialogResultData> {
  data = inject<WDORegistrationDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        WDORegistrationComponent,
        AbstractDialogResultData<WDORegistrationDialogResultData>
      >
    >(MatDialogRef);
  private readonly locale = inject(LocaleService);
  private cycleService = inject(CycleService);
  private store = inject<Store<RootState>>(Store);
  private formBuilder = inject(FormBuilder);

  startDate: Date;
  endDate: Date;
  tabs: Array<{
    name: string;
    feedIntakeSuffix: string;
    waterIntakeSuffix: string;
    feedIntakePlaceholder: string;
    waterIntakePlaceholder: string;
    showFields?: string[];
    chickAgeInDays?: number;
  }> = [];
  formGroups: FormGroup[] = [];
  weeks: WeekRegistration[];
  weeksForAllBarns: WeekRegistration[];
  selectedWeekNr: number;
  previousWeekNr: number;
  selectedWeek: WeekRegistration;
  barns: Barn[];
  selectedBarnId: number;
  selectedFlock: DayOldChick;
  formGroupsValueChanged = false;
  allSettingsAreOff: boolean;
  oneDayTime = 1000 * 60 * 60 * 24;
  selectedTabName: string;
  selectedTabIndex = 0;
  dayOffset$: Observable<number> = this.store.select(getDayOffset);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();

    // Translate the title.
    this.data.title = `${this.locale.translate('cycle')} ${
      this.data.cycle.cycleNumber
    } - ${this.locale.translate('wdo-registration.registration')}`;

    // using default settings if they are not set in the cycle (which is possible)
    if (!this.data.cycle.observationSettings) {
      this.data.cycle = {
        ...this.data.cycle,
        observationSettings: {
          mortality: WDOsettingEnum.Off,
          feedIntake: WDOsettingEnum.Off,
          waterIntake: WDOsettingEnum.Off,
          indoorTemperature: WDOsettingEnum.Off,
          outdoorTemperature: WDOsettingEnum.Off,
          indoorHumidity: WDOsettingEnum.Off,
          outdoorHumidity: WDOsettingEnum.Off,
          weight: WDOsettingEnum.Off,
          litterQuality: WDOsettingEnum.Off,
        },
      };
    }

    // check if all settings are off, because then we show a message to the user
    this.allSettingsAreOff =
      !this.data.cycle.observationSettings || this.getShowFields(WDOsettingEnum.Off).length === 9;

    // get unique barns for selector and pre-select first one if any
    if (!!this.data.barns && !!this.data.selectedBarn) {
      this.barns = this.data.barns;
      this.selectedBarnId = this.data.selectedBarn.id;
    } else {
      this.barns = [...this.data.cycle.dayOldChicks]
        .map((doc) => doc.barn)
        .filter(
          (barn, index, self) =>
            index ===
            self.findIndex(
              (t) => t.id === barn.id && t.name === barn.name && t.surfaceArea === barn.surfaceArea,
            ),
        );
      this.selectedBarnId = !!this.barns && this.barns.length > 0 && this.barns[0].id;
    }

    // check if there are weeks for this flock, otherwise they must be added..
    this.addWeeksForEachBarn();

    this.setSelectedFlock(this.selectedBarnId);
  }

  setSelectedFlock(barnId: number): void {
    // preventively persist the week before switching
    if (this.selectedWeek) {
      this.persistWeek(this.selectedWeek.weekNr, this.selectedWeek.barnId);
    }
    this.selectedFlock = this.data.cycle.dayOldChicks.find((doc) => doc.barn.id === barnId);

    // Sort week registrations.
    this.weeks = this.weeksForAllBarns
      .filter((week) => week.barnId === barnId)
      .sort((w1, w2) => (w1.weekNr > w2.weekNr ? 1 : -1));

    if (this.selectedWeekNr) {
      this.setSelectedWeek(this.selectedWeekNr, barnId);
    } else {
      this.setSelectedWeek(
        Math.max(...this.weeks.filter((week) => week.barnId === barnId).map((week) => week.weekNr)),
        barnId,
      );
    }
  }

  getInputMethod(): string {
    let inputDefinitionArray = Object.values(this.data.cycle.observationSettings) as string[];
    const daysOfWeek = Object.values(DayOfWeekEnum);
    inputDefinitionArray = inputDefinitionArray
      .filter((element) => daysOfWeek.findIndex((day) => day === element) === -1)
      .filter((element) => element !== null && element !== WDOsettingEnum.Off);
    const isAllSame =
      inputDefinitionArray.filter((v, _, a) => v === a[0]).length === inputDefinitionArray.length;
    return isAllSame ? inputDefinitionArray[0] : 'advanced';
  }

  getShowFields(filterValue?: WDOsettingEnum): string[] {
    const inputDefinitionKeys = Object.keys(this.data.cycle.observationSettings);
    return inputDefinitionKeys.filter((key: string) => {
      const foundValue = (
        (<unknown>this.data.cycle.observationSettings) as {
          [key: string]: string;
        }
      )[key];
      return (
        (filterValue === undefined || foundValue === filterValue) &&
        (filterValue === WDOsettingEnum.Off || foundValue !== WDOsettingEnum.Off.toString())
      );
    });
  }

  getWeekInfo(week: WeekRegistration): string {
    return this.cycleService.getWeekInfo(
      this.data.cycle.cycleDate,
      this.data.cycle.cycleEndDate,
      week.weekNr,
      week.startDay,
      week.endDay,
    );
  }

  setSelectedWeek(
    selectedWeekNr: number,
    barnId: number,
    event?: Event,
    isNextDayEvent = false,
  ): void {
    this.selectedWeekNr = selectedWeekNr;
    this.selectedWeek = this.weeksForAllBarns.find(
      (week) => week.weekNr === selectedWeekNr && week.barnId === barnId,
    );
    if (this.getInputMethod() !== WDOsettingEnum.PerWeek) {
      this.createTabs();
    }

    if (this.formGroups.length === 0) {
      if (this.getInputMethod() !== WDOsettingEnum.PerWeek) {
        // First time: Create form group for each day and fill any data available.
        for (let tab = 0; tab < this.tabs.length; tab++) {
          if (this.tabs[tab].name.startsWith('week')) {
            this.createFormGroup(this.selectedWeek.registrationData);
          } else {
            this.createFormGroup(this.selectedWeek.dayRegistrations[tab].registrationData);
          }
        }
      } else {
        this.createFormGroup(this.selectedWeek.registrationData);
      }
    } else {
      // 'save' the data from the formGroups to this.weeks before switching to the new week
      if (!!event || isNextDayEvent) {
        this.persistWeek(this.previousWeekNr, barnId);
      }
      const emptyRegData = {
        mortality: null,
        selection: null,
        selectionSmall: null,
        selectionLegProblems: null,
        feedIntakeInKg: null,
        waterIntakeInL: null,
        indoorTemperatureInCelsius: null,
        outdoorTemperatureInCelsius: null,
        indoorHumidityInPercentages: null,
        outdoorHumidityInPercentages: null,
        chickWeightInGrams: null,
        litterQualityLoosePercentage: null,
        litterQualityCakedPercentage: null,
        litterQualityWetPercentage: null,
        unitOfMeasureSettings: this.data.unitOfMeasureSettings,
      } as ValueRegistration;
      if (this.getInputMethod() !== WDOsettingEnum.PerWeek) {
        // patchValues for all fields with the data of the new selected week
        for (let tab = 0; tab < this.tabs.length; tab++) {
          let regData = this.selectedWeek.dayRegistrations[tab]?.registrationData;
          // if the selectedWeek has no dayRegs yet, then an empty regData object must be set
          if (this.tabs[tab].name.startsWith('week')) {
            regData = this.selectedWeek.registrationData;
          }
          if (!!regData && Object.keys(regData).length === 0 && regData.constructor === Object) {
            regData = emptyRegData;
          }
          if (this.formGroups[tab]) {
            const formValue = {
              ...regData,
              unitOfMeasureSettings: this.data.unitOfMeasureSettings,
            };
            this.formGroups[tab].setValue(formValue, { emitEvent: false });
          } else {
            this.createFormGroup(regData);
          }
        }
      } else {
        let regData = this.selectedWeek.registrationData;
        if (!!regData && Object.keys(regData).length === 0 && regData.constructor === Object) {
          regData = emptyRegData;
        }
        if (this.formGroups[0]) {
          const formValue = {
            ...regData,
            unitOfMeasureSettings: this.data.unitOfMeasureSettings,
          };
          this.formGroups[0].setValue(formValue, { emitEvent: false });
        } else {
          this.createFormGroup(regData);
        }
      }
    }
    // remember the weeknr for the next week switch
    this.previousWeekNr = selectedWeekNr;
  }

  public addWeeksForEachBarn() {
    // get the weeks that are already there
    this.weeksForAllBarns = [...this.data.weekRegistrations];

    // If there is no week found, we need to create new weeks between cycleDate and today's date.
    this.barns.forEach((barn) => {
      // determine the amount of weeks needed
      let startDay = 0;

      const cycleStartTime =
        new Date(this.data.cycle.cycleDate).getTime() + startDay * this.oneDayTime;
      const cycleEndTime = this.data.cycle.cycleEndDate
        ? new Date(this.data.cycle.cycleEndDate).getTime()
        : new Date().getTime();
      const nrOfWeeks = Math.ceil((cycleEndTime - cycleStartTime) / (this.oneDayTime * 7));

      for (let weekNr = 1; weekNr <= nrOfWeeks; weekNr++) {
        const foundWeekRegistration = this.data.weekRegistrations.find(
          (week) => week.weekNr === weekNr && week.barnId === barn.id,
        );

        // in first week, the startDay cannot be before cycleDate
        let nrDaysInWeek = 7;
        if (weekNr === 1 && startDay < 0) {
          nrDaysInWeek += startDay;
          startDay = 0;
        }

        const dayRegistrations: DayRegistration[] = [];
        for (let i = 0; i < nrDaysInWeek; i++) {
          dayRegistrations.push({
            day: startDay + i,
            registrationData: {},
          });
        }

        // Create an array of day registrations when array is empty.
        if (foundWeekRegistration && foundWeekRegistration.dayRegistrations.length === 0) {
          const index = this.weeksForAllBarns.findIndex(
            (x) => x.weekNr === foundWeekRegistration.weekNr,
          );
          this.weeksForAllBarns[index] = {
            ...foundWeekRegistration,
            dayRegistrations,
          };
        }

        // Create an empty new week registration instance.
        if (!foundWeekRegistration) {
          this.weeksForAllBarns.push({
            weekNr,
            dayRegistrations,
            registrationData: {},
            startDay,
            endDay: startDay + 6,
            barnId: barn.id,
          });
        }
        startDay = startDay + 7;
      }
    });
  }

  private persistWeek(weekNr: number, barnId: number) {
    if (this.formGroupsValueChanged) {
      let previousWeek: WeekRegistration = this.weeks.find(
        (week) => week.weekNr === weekNr && week.barnId === barnId,
      );
      if (previousWeek) {
        previousWeek = Object.assign({}, this.getDataFromForms(previousWeek));

        this.weeksForAllBarns = [
          ...this.weeksForAllBarns.filter((w) => !(w.weekNr === weekNr && w.barnId === barnId)),
          previousWeek,
        ];

        // update this.weeks for current barn and sort week registrations.
        this.weeks = this.weeksForAllBarns
          .filter((week) => week.barnId === barnId)
          .sort((w1, w2) => (w1.weekNr > w2.weekNr ? 1 : -1));
      }
      this.formGroupsValueChanged = false;
    }
  }

  private createTabs() {
    if (!!this.selectedWeek?.dayRegistrations && this.selectedWeek.dayRegistrations.length > 0) {
      this.tabs = [];

      const waterIntakeUnitLabels = this.getWaterIntakeUnitLabels();
      const feedIntakeUnitLabels = this.getFeedIntakeUnitLabels();

      this.selectedWeek.dayRegistrations.forEach((day: DayRegistration) => {
        const cycleEndDate: Date = Utils.toUtcDate(
          new Date(new Date(this.data.cycle.cycleEndDate).setUTCHours(0, 0, 0, 0)),
        );
        const cycleDate: Date = Utils.toUtcDate(
          new Date(new Date(this.data.cycle.cycleDate).setUTCHours(0, 0, 0, 0)),
        );
        cycleDate.setDate(cycleDate.getDate() + day.day);

        if (!this.data.cycle.cycleEndDate || cycleDate.getTime() <= cycleEndDate.getTime()) {
          this.tabs.push({
            name: formatDate(cycleDate, 'd MMM yy', this.locale.getLocale()),
            showFields: this.getShowFields(WDOsettingEnum.PerDay),
            waterIntakeSuffix: waterIntakeUnitLabels.suffix,
            feedIntakeSuffix: feedIntakeUnitLabels.suffix,
            feedIntakePlaceholder: feedIntakeUnitLabels.placeholder,
            waterIntakePlaceholder: waterIntakeUnitLabels.placeholder,
            chickAgeInDays: this.getChickAgeInDays(this.data.cycle, day.day, this.selectedBarnId),
          });
        }
      });

      if (this.getInputMethod() === 'advanced') {
        this.tabs.push({
          name: `week ${this.selectedWeek.weekNr}`,
          showFields: this.getShowFields(WDOsettingEnum.PerWeek),
          waterIntakeSuffix: waterIntakeUnitLabels.suffix,
          feedIntakeSuffix: feedIntakeUnitLabels.suffix,
          feedIntakePlaceholder: feedIntakeUnitLabels.placeholder,
          waterIntakePlaceholder: waterIntakeUnitLabels.placeholder,
          chickAgeInDays: this.getChickAgeInDays(
            this.data.cycle,
            this.selectedWeek.endDay,
            this.selectedBarnId,
          ),
        });
      }

      this.selectedTabName = this.tabs[0]?.name;
      this.selectedTabIndex = 0;
    }
  }

  setSelectedDay(selectedTabName: string) {
    this.selectedTabIndex = this.tabs.findIndex((t) => t.name === selectedTabName);
  }

  private createFormGroup(data: ValueRegistration) {
    this.formGroups.push(
      this.formBuilder.group(
        {
          mortality: [data?.mortality],
          selection: [data?.selection],
          selectionSmall: [data?.selectionSmall],
          selectionLegProblems: [data?.selectionLegProblems],
          feedIntakeInKg: [data?.feedIntakeInKg],
          waterIntakeInL: [data?.waterIntakeInL],
          indoorTemperatureInCelsius: [data?.indoorTemperatureInCelsius],
          outdoorTemperatureInCelsius: [data?.outdoorTemperatureInCelsius],
          indoorHumidityInPercentages: [data?.indoorHumidityInPercentages],
          outdoorHumidityInPercentages: [data?.outdoorHumidityInPercentages],
          chickWeightInGrams: [data?.chickWeightInGrams],
          litterQualityLoosePercentage: [data?.litterQualityLoosePercentage],
          litterQualityCakedPercentage: [data?.litterQualityCakedPercentage],
          litterQualityWetPercentage: [data?.litterQualityWetPercentage],
          unitOfMeasureSettings: [this.data.unitOfMeasureSettings],
        },
        {
          validator: litterQualityValidator(
            'litterQualityLoosePercentage',
            'litterQualityCakedPercentage',
            'litterQualityWetPercentage',
          ),
        },
      ),
    );
    this.formGroups.forEach((formgroup) => {
      formgroup.valueChanges.subscribe((_) => (this.formGroupsValueChanged = true));
    });
  }

  private getFormGroupData(index: number): ValueRegistration {
    return this.formGroups[index] ? (this.formGroups[index].value as ValueRegistration) : {};
  }

  private getDataFromForms(week: WeekRegistration): WeekRegistration {
    const newWeekRegistration: WeekRegistration = {
      dayRegistrations: [],
      endDay: week.endDay,
      startDay: week.startDay,
      weekNr: week.weekNr,
      registrationData: {},
      barnId: week.barnId,
    };

    if (this.getInputMethod() === WDOsettingEnum.PerWeek) {
      newWeekRegistration.registrationData = { ...this.getFormGroupData(0) };
    } else {
      const dayTabs = this.tabs.length - (this.getInputMethod() === 'advanced' ? 2 : 1);
      for (let i = 0; i <= dayTabs; i++) {
        newWeekRegistration.dayRegistrations.push({
          day: newWeekRegistration.startDay + i,
          registrationData: i < this.formGroups.length ? this.getFormGroupData(i) : {},
        });
      }
      // if 'advanced settings' then PerWeek-info is on last tab
      if (this.getInputMethod() === 'advanced') {
        newWeekRegistration.registrationData = { ...this.getFormGroupData(this.tabs.length - 1) };
      }
    }
    return newWeekRegistration;
  }

  getFeedIntakeUnitLabels() {
    if (this.data.unitOfMeasureSettings.feedIntakeUnit === FeedIntakeUnit.GramPerBird) {
      return {
        suffix: this.locale.translate(`wdo-labels.gramSuffix`),
        placeholder: this.locale.translate(`wdo-labels.feedIntakeGramPerBird`),
      };
    }

    return {
      suffix: this.locale.translate(`wdo-labels.kilogramSuffix`),
      placeholder: this.locale.translate(`wdo-labels.feedIntakeKg`),
    };
  }

  getWaterIntakeUnitLabels() {
    if (this.data.unitOfMeasureSettings.waterIntakeUnit === WaterIntakeUnit.MililiterPerBird) {
      return {
        suffix: this.locale.translate(`wdo-labels.mililiterSuffix`),
        placeholder: this.locale.translate(`wdo-labels.waterIntakeMililiterPerBird`),
      };
    }

    return {
      suffix: this.locale.translate(`wdo-labels.literSuffix`),
      placeholder: this.locale.translate(`wdo-labels.waterIntakeL`),
    };
  }

  private getChickAgeInDays(cycle: Cycle, day: number, barnId: number): number {
    const chickBirthDate = this.cycleService.getChickBirthDateInSection(cycle, barnId);
    const registrationDate = this.cycleService.getDateOfDayInCycle(cycle, day);
    let dayOffsetSettings = 0;

    this.dayOffset$.pipe(take(1)).subscribe((dayOffset) => {
      if (dayOffset) {
        dayOffsetSettings = Number(dayOffset);
      }
    });

    if (
      chickBirthDate &&
      registrationDate &&
      chickBirthDate.getTime() < registrationDate.getTime()
    ) {
      const ageInDays: number = DateHelper.getDaysBetween(chickBirthDate, registrationDate);

      return ageInDays + dayOffsetSettings;
    }

    return undefined;
  }

  nextDay() {
    if (this.tabs[this.selectedTabIndex + 1]) {
      this.selectedTabIndex++;
      this.selectedTabName = this.tabs[this.selectedTabIndex].name;
    } else {
      this.selectedWeekNr++;
      this.setSelectedWeek(this.selectedWeekNr, this.selectedBarnId, null, true);
    }
  }

  updateAltButtonVisiblity() {
    this.data.altButton =
      !(
        this.weeks?.length === this.selectedWeekNr &&
        this.tabs?.length - 1 === this.selectedTabIndex
      ) && this.getInputMethod() !== 'PerWeek';
  }

  save() {
    let validFormGroups = true;
    this.formGroups.forEach((formGroup) => {
      if (formGroup.errors !== null && formGroup.errors['litterQualityInvalid']) {
        validFormGroups = false;
      }
    });

    if (validFormGroups) {
      // preventively persist the week before switching
      if (this.selectedWeek) {
        this.persistWeek(this.selectedWeek.weekNr, this.selectedWeek.barnId);
      }

      const cycle = {
        ...this.data.cycle,
        weekRegistrations: this.weeksForAllBarns,
      };

      const diffWeekRegistrations = this.weeksForAllBarns.filter(
        (item) => this.data.weekRegistrations.indexOf(item) < 0,
      );

      const filteredDiffWeekRegistrations = diffWeekRegistrations.filter(
        (week) =>
          Object.keys(week.registrationData).length > 0 ||
          Object.keys(week.dayRegistrations).length > 0,
      );

      filteredDiffWeekRegistrations.forEach((week) =>
        week.dayRegistrations.filter((day) => Object.keys(day.registrationData).length > 0),
      );

      const observations = this.cycleService.convertWeekRegistrationsToObservationsRegistration(
        filteredDiffWeekRegistrations,
        this.data.cycle.id,
      );

      // Save to store and confirm.
      this.store.dispatch(
        upsertObservationsForCycleRequest({
          observations,
          unitOfMeasureSettings: this.data.unitOfMeasureSettings,
        }),
      );
      this.confirm({ cycle });
    }
  }
}
